/**
 * Geometry View builder
 *
 * Turns any drawn/clicked geometry (a saved feature or an ephemeral measure
 * line) into the data the context-bar "View" surface renders: a collapsed stat
 * strip plus geometry-appropriate widgets — elevation profile (lines),
 * elevation/slope/land-cover distributions, and an aspect rose.
 *
 * Lines sample terrain along the path; areas (polygon/circle/rectangle) sample a
 * capped grid of points inside the ring. Points just report coords + elevation.
 */

import {
  length as turfLength,
  area as turfArea,
  along,
  bearing as turfBearing,
  lineString,
  polygon as turfPolygon,
  bbox as turfBbox,
  booleanPointInPolygon,
} from '@turf/turf'
import type { ProfilePoint } from '../components/map/elevation-profile.js'
import type { AspectCounts } from '../components/map/track-aspect-rose.js'
import {
  convertDistance,
  convertArea,
  convertElevation,
  formatBearing,
  type UnitSystem,
} from '../utils/units.js'
import { LAND_COVER_CLASSES } from '../services/terrain/land-cover-types.js'

export type ViewGeometryType = 'line' | 'polygon' | 'point'

export interface GeometrySummaryItem {
  label: string
  value: string
}

/** One labeled, colored bar in a distribution chart. */
export interface DistributionBin {
  /** Axis label under the bar; '' suppresses it (continuous charts label sparsely). */
  label: string
  /** Count of samples in this bin. */
  value: number
  /** CSS color for the bar. */
  color: string
  /** Optional hover description (e.g. a range); the chart prefixes the percent. */
  hint?: string
}

/** Which widget slots are still being sampled (show a spinner). A slot that is
 *  not pending and has no data is genuinely empty and hidden. */
export interface ViewPending {
  profile: boolean
  elevation: boolean
  slope: boolean
  aspect: boolean
  landCover: boolean
  treeline: boolean
}

export interface GeometryView {
  type: ViewGeometryType
  /** Display title for the collapsed bar (feature name or "Measurement"). */
  title: string
  /** Collapsed-bar stat strip (distance, gain, slope, tree %, …). */
  stats: GeometrySummaryItem[]
  /** Elevation profile (lines only; empty otherwise). */
  profile: ProfilePoint[]
  /** Elevation distribution (nice fixed-width bands). */
  elevationHistogram: DistributionBin[]
  /** Sparse axis labels [start, mid, end] for the elevation histogram. */
  elevationAxis: string[]
  /** Slope-angle distribution in hiker difficulty bands (Tobler-anchored). */
  slopeHistogram: DistributionBin[]
  /** Aspect distribution (8 compass bins). */
  aspectCounts: AspectCounts
  /** Land-cover distribution (ESA WorldCover classes), colored by class. */
  landCover: DistributionBin[]
  /** % of the feature below / near / above its local treeline. */
  treelineZones: DistributionBin[]
  /** Per-slot loading state — slots still sampling render a spinner. */
  pending: ViewPending
  /** Ephemeral measures are dismiss-and-discard; saved features are not. */
  ephemeral: boolean
}

const NO_PENDING: ViewPending = {
  profile: false,
  elevation: false,
  slope: false,
  aspect: false,
  landCover: false,
  treeline: false,
}

/** DEM-derived per-point terrain — satisfied by `elevationPointService`. */
export interface TerrainSampler {
  getElevation(lat: number, lng: number): Promise<number | null>
  getAspect(lat: number, lng: number): Promise<number | null>
  getSlopeVector(lat: number, lng: number): Promise<{ grade: number } | null>
  getElevationGrid(lats: number[], lngs: number[]): Promise<(number | null)[]>
}

/** Bulk land-cover read — satisfied by `landCoverService`. Returns a row-major
 *  (row 0 = north) class raster over the bbox; 0 means no data. */
export interface LandCoverSampler {
  queryClassesInBbox(
    bounds: [number, number, number, number],
    width: number,
    height: number
  ): Promise<Uint8Array>
}

/** Treeline-elevation read — satisfied by `treelineCogService`. Returns treeline
 *  elevation (m) over a lat/lng mesh (lats north→south, lngs west→east),
 *  row-major; `null` where there's no treeline / no coverage. */
export interface TreelineSampler {
  getTreelineGrid(lats: number[], lngs: number[]): Promise<(number | null)[]>
}

const PROFILE_SAMPLE_M = 25
const PROFILE_MIN_SAMPLES = 60
const LINE_MAX_SAMPLES = 400
/** Max grid points sampled inside an area — keeps area views responsive. */
const AREA_MAX_SAMPLES = 256
const AREA_GRID = 20 // up to AREA_GRID×AREA_GRID candidate points before the cap
/** Land cover is read once over the geometry's bbox (~40 m), capped per axis,
 *  then sampled locally — far cheaper than a COG read per point. */
const LANDCOVER_MAX_PX = 512
const METERS_PER_DEG = 111_320
const LANDCOVER_RES_M = 40

const ZERO_ASPECT: AspectCounts = [0, 0, 0, 0, 0, 0, 0, 0]

/** Hiker difficulty bands by slope angle, with thresholds anchored to Tobler
 *  walking-pace breakpoints and a green→red effort ramp. Deliberately distinct
 *  from the avalanche slope-shading map layer (which lumps everything <27° and
 *  serves a different audience). Labels stay numeric so they fit under narrow
 *  bars; the difficulty word + pace live in the hover hint, and the color
 *  carries the easy→hard read at a glance. */
const SLOPE_BANDS: { min: number; max: number; label: string; color: string; hint: string }[] = [
  { min: 0, max: 4, label: '0–4°', color: 'rgb(46,160,67)', hint: 'Flat — easy walking' },
  { min: 4, max: 8, label: '4–8°', color: 'rgb(140,190,40)', hint: 'Gentle — easy climb' },
  { min: 8, max: 14, label: '8–14°', color: 'rgb(234,179,8)', hint: 'Moderate — steady effort' },
  {
    min: 14,
    max: 20,
    label: '14–20°',
    color: 'rgb(249,115,22)',
    hint: 'Steep — strenuous, switchbacks',
  },
  {
    min: 20,
    max: 30,
    label: '20–30°',
    color: 'rgb(239,68,68)',
    hint: 'Very steep — grind, hands likely',
  },
  {
    min: 30,
    max: Infinity,
    label: '30°+',
    color: 'rgb(159,18,57)',
    hint: 'Scramble — beyond hiking',
  },
]

function slopeAngleFromGrade(grade: number): number {
  return (Math.atan(grade) * 180) / Math.PI
}

function rgb([r, g, b]: [number, number, number]): string {
  return `rgb(${r},${g},${b})`
}

/** Even-interval sample positions (lat/lng + distance-from-start) along a line. */
function sampleLine(
  coords: number[][],
  sampleM: number,
  minSamples: number,
  maxSamples: number
): { lat: number; lng: number; distance: number }[] {
  const line = lineString(coords)
  const totalKm = turfLength(line, { units: 'kilometers' })
  const totalM = totalKm * 1000
  const n = Math.min(maxSamples, Math.max(minSamples, Math.ceil(totalM / sampleM)))
  const stepKm = totalKm / n
  const out: { lat: number; lng: number; distance: number }[] = []
  for (let i = 0; i <= n; i++) {
    const distKm = i * stepKm
    const [lng, lat] = along(line, distKm, { units: 'kilometers' }).geometry.coordinates
    out.push({ lat, lng, distance: distKm * 1000 })
  }
  return out
}

/** A capped grid of points lying inside an area geometry. */
function samplePolygon(geometry: GeoJSON.Geometry): { lat: number; lng: number }[] {
  const poly =
    geometry.type === 'Polygon'
      ? turfPolygon(geometry.coordinates)
      : geometry.type === 'MultiPolygon'
        ? turfPolygon(geometry.coordinates[0] ?? [])
        : null
  if (!poly) return []
  const [west, south, east, north] = turfBbox(poly)
  const out: { lat: number; lng: number }[] = []
  const dx = (east - west) / (AREA_GRID + 1)
  const dy = (north - south) / (AREA_GRID + 1)
  for (let i = 1; i <= AREA_GRID; i++) {
    for (let j = 1; j <= AREA_GRID; j++) {
      const lng = west + dx * i
      const lat = south + dy * j
      if (booleanPointInPolygon([lng, lat], poly)) out.push({ lat, lng })
    }
  }
  // Cap (evenly) so a large area doesn't explode the sample count.
  if (out.length > AREA_MAX_SAMPLES) {
    const stride = Math.ceil(out.length / AREA_MAX_SAMPLES)
    return out.filter((_, i) => i % stride === 0)
  }
  return out
}

// A single sampler error must not sink the whole View — swallow to null.
function safe<T>(pr: Promise<T | null>): Promise<T | null> {
  return pr.catch(() => null)
}

/** Fast phase — DEM-derived elevation/slope/aspect (terrarium tiles, mostly cached). */
async function gatherDEM(
  points: { lat: number; lng: number }[],
  terrain: TerrainSampler
): Promise<{ elevations: (number | null)[]; slopes: number[]; aspects: number[] }> {
  const [elev, slopeVecs, aspectVals] = await Promise.all([
    Promise.all(points.map(p => safe(terrain.getElevation(p.lat, p.lng)))),
    Promise.all(points.map(p => safe(terrain.getSlopeVector(p.lat, p.lng)))),
    Promise.all(points.map(p => safe(terrain.getAspect(p.lat, p.lng)))),
  ])
  const slopes: number[] = []
  for (const v of slopeVecs) if (v) slopes.push(slopeAngleFromGrade(v.grade))
  const aspects: number[] = []
  for (const a of aspectVals) if (a != null) aspects.push(a)
  return { elevations: elev, slopes, aspects }
}

/**
 * Slow phase — land cover. Reads the geometry's bbox once (a few COG range reads
 * at ~40 m) into an in-memory raster, then samples every point locally.
 */
async function gatherLandCover(
  points: { lat: number; lng: number }[],
  landCover: LandCoverSampler
): Promise<number[]> {
  if (!points.length) return []
  let west = Infinity
  let south = Infinity
  let east = -Infinity
  let north = -Infinity
  for (const p of points) {
    if (p.lng < west) west = p.lng
    if (p.lng > east) east = p.lng
    if (p.lat < south) south = p.lat
    if (p.lat > north) north = p.lat
  }
  const wDeg = Math.max(east - west, 1e-4)
  const hDeg = Math.max(north - south, 1e-4)
  const clampPx = (deg: number): number =>
    Math.max(1, Math.min(LANDCOVER_MAX_PX, Math.round((deg * METERS_PER_DEG) / LANDCOVER_RES_M)))
  const W = clampPx(wDeg)
  const H = clampPx(hDeg)

  let raster: Uint8Array
  try {
    raster = await landCover.queryClassesInBbox([west, south, east, north], W, H)
  } catch {
    return []
  }

  const classes: number[] = []
  for (const p of points) {
    let col = Math.floor(((p.lng - west) / wDeg) * W)
    let row = Math.floor(((north - p.lat) / hDeg) * H)
    if (col < 0) col = 0
    else if (col >= W) col = W - 1
    if (row < 0) row = 0
    else if (row >= H) row = H - 1
    const c = raster[row * W + col]
    if (c > 0) classes.push(c) // 0 = no data
  }
  return classes
}

function buildAspectCounts(aspects: number[]): AspectCounts {
  const counts: AspectCounts = [0, 0, 0, 0, 0, 0, 0, 0]
  for (const a of aspects) counts[Math.round(a / 45) % 8]++
  return counts
}

function buildSlopeHistogram(slopes: number[]): DistributionBin[] {
  const bins = SLOPE_BANDS.map(b => ({ label: b.label, value: 0, color: b.color, hint: b.hint }))
  for (const s of slopes) {
    const idx = SLOPE_BANDS.findIndex(b => s >= b.min && s < b.max)
    if (idx >= 0) bins[idx].value++
  }
  return bins.some(b => b.value > 0) ? bins : []
}

const ELEV_TARGET_BINS = 10

/** Round a raw bin width up to a clean multiple appropriate to its magnitude
 *  (50s in the hundreds, 5s in the tens, …) so bin edges land on round numbers:
 *  e.g. a 3000 ft range → 300 ft bins, a 500 ft range → 50 ft bins. */
function niceBinWidth(raw: number): number {
  if (raw <= 0) return 1
  const pow = Math.pow(10, Math.floor(Math.log10(raw)))
  const step = pow / 2 // 50 for hundreds, 5 for tens, 0.5 for ones
  return Math.max(step, Math.round(raw / step) * step)
}

/** Elevation distribution with fixed, round bin widths (display units) and a
 *  sparse [start, mid, end] axis so labels never need truncating. */
function buildElevationHistogram(
  elevationsM: number[],
  unitSystem: UnitSystem
): { bins: DistributionBin[]; axis: string[] } {
  if (elevationsM.length < 2) return { bins: [], axis: [] }
  const factor = unitSystem === 'imperial' ? 3.28084 : 1
  const unit = unitSystem === 'imperial' ? 'ft' : 'm'
  const vals = elevationsM.map(e => e * factor)
  const min = Math.min(...vals)
  const max = Math.max(...vals)
  if (max - min < 1) return { bins: [], axis: [] }

  const width = niceBinWidth((max - min) / ELEV_TARGET_BINS)
  const start = Math.floor(min / width) * width
  const end = Math.ceil(max / width) * width
  const n = Math.max(1, Math.round((end - start) / width))
  const fmt = (v: number): string => `${Math.round(v).toLocaleString()} ${unit}`

  const counts = new Array<number>(n).fill(0)
  for (const v of vals) {
    let idx = Math.floor((v - start) / width)
    if (idx < 0) idx = 0
    if (idx >= n) idx = n - 1
    counts[idx]++
  }
  const bins: DistributionBin[] = counts.map((c, i) => {
    const lo = start + i * width
    return {
      label: '',
      value: c,
      color: 'rgb(120,160,140)',
      hint: `between ${fmt(lo)} and ${fmt(lo + width)}`,
    }
  })
  return { bins, axis: [fmt(start), fmt((start + end) / 2), fmt(end)] }
}

function buildLandCover(classes: number[]): DistributionBin[] {
  const counts = new Map<number, number>()
  for (const c of classes) counts.set(c, (counts.get(c) ?? 0) + 1)
  const bins: DistributionBin[] = []
  for (const cls of LAND_COVER_CLASSES) {
    const n = counts.get(cls.value)
    if (n) bins.push({ label: cls.name, value: n, color: rgb(cls.color) })
  }
  return bins.sort((a, b) => b.value - a.value)
}

/** Total ascent/descent (meters) over a profile. */
function computeGainLoss(profile: ProfilePoint[]): { ascent: number; descent: number } {
  let ascent = 0
  let descent = 0
  for (let i = 1; i < profile.length; i++) {
    const d = profile[i].elevation - profile[i - 1].elevation
    if (d > 0) ascent += d
    else descent -= d
  }
  return { ascent, descent }
}

/** Tobler's hiking function — walking speed (km/h) for a signed grade (rise/run);
 *  peaks just below flat (~−5%) and falls off ~exponentially with steepness either
 *  way, so up- and downhill are weighted differently. */
function toblerSpeedKmh(grade: number): number {
  return 6 * Math.exp(-3.5 * Math.abs(grade + 0.05))
}

/** Estimated moving time (seconds) along an elevation profile via Tobler, using
 *  the signed along-path grade of each segment. Distances are horizontal meters. */
function estimateHikeSeconds(profile: ProfilePoint[]): number {
  let seconds = 0
  for (let i = 1; i < profile.length; i++) {
    const dDist = profile[i].distance - profile[i - 1].distance
    if (dDist <= 0) continue
    const grade = (profile[i].elevation - profile[i - 1].elevation) / dDist
    const speedMs = (toblerSpeedKmh(grade) * 1000) / 3600
    if (speedMs > 0) seconds += dDist / speedMs
  }
  return seconds
}

function formatDuration(seconds: number): string {
  const mins = Math.round(seconds / 60)
  if (mins < 60) return `${mins} min`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m ? `${h}h ${m}m` : `${h}h`
}

function treeCoverPct(classes: number[]): number | null {
  if (!classes.length) return null
  const tree = classes.filter(c => c === 10).length
  return Math.round((tree / classes.length) * 100)
}

function lineCoords(geometry: GeoJSON.Geometry): number[][] {
  if (geometry.type === 'LineString') return geometry.coordinates
  if (geometry.type === 'MultiLineString') return geometry.coordinates.flat()
  return []
}

/** Sample an elevation profile at even intervals along a line. */
export async function sampleElevationAlongLine(
  coords: number[][],
  sampler: Pick<TerrainSampler, 'getElevation'>
): Promise<ProfilePoint[]> {
  if (coords.length < 2) return []
  const pts = sampleLine(coords, PROFILE_SAMPLE_M, PROFILE_MIN_SAMPLES, LINE_MAX_SAMPLES)
  const elevations = await Promise.all(
    pts.map(p => sampler.getElevation(p.lat, p.lng).catch(() => null))
  )
  const profile: ProfilePoint[] = []
  for (let i = 0; i < pts.length; i++) {
    const e = elevations[i]
    if (e != null) profile.push({ distance: pts[i].distance, elevation: e })
  }
  return profile
}

const EMPTY_DISTRIBUTIONS = {
  profile: [] as ProfilePoint[],
  elevationHistogram: [] as DistributionBin[],
  elevationAxis: [] as string[],
  slopeHistogram: [] as DistributionBin[],
  aspectCounts: [...ZERO_ASPECT] as AspectCounts,
  landCover: [] as DistributionBin[],
  treelineZones: [] as DistributionBin[],
}

/** ±half-width of the near-treeline band (m). */
const TREELINE_NTL_BUFFER_M = 100
/** Padding (deg) around a feature when sampling for the treeline surface, so a
 *  forested feature still "sees" the open terrain above it. */
const TREELINE_PAD_DEG = 0.02
const TREELINE_SURFACE_GRID = 96
const ZONE_BELOW_COLOR = 'rgb(34,160,90)'
const ZONE_NEAR_COLOR = 'rgb(245,170,30)'
const ZONE_ABOVE_COLOR = 'rgb(60,130,240)'

/**
 * Classify a feature's sample points into Below / Near / Above treeline, against
 * a *spatially-varying* treeline surface built from a buffered area around it.
 * Returns 3 bins, or [] if the treeline can't be determined nearby.
 */
async function buildTreelineZones(
  geometry: GeoJSON.Geometry,
  samples: { lat: number; lng: number; elevation: number | null }[],
  treeline: TreelineSampler
): Promise<DistributionBin[]> {
  const [w0, s0, e0, n0] = turfBbox(geometry)
  const w = w0 - TREELINE_PAD_DEG
  const s = s0 - TREELINE_PAD_DEG
  const e = e0 + TREELINE_PAD_DEG
  const n = n0 + TREELINE_PAD_DEG
  const G = TREELINE_SURFACE_GRID
  const lats: number[] = []
  const lngs: number[] = []
  for (let i = 0; i < G; i++) lats.push(n - (n - s) * (i / (G - 1)))
  for (let j = 0; j < G; j++) lngs.push(w + (e - w) * (j / (G - 1)))

  let grid: (number | null)[]
  try {
    grid = await treeline.getTreelineGrid(lats, lngs)
  } catch {
    return []
  }

  // Bilinearly sample the COG treeline grid at an arbitrary feature point.
  const treelineAt = (lat: number, lng: number): number | null => {
    const fy = ((n - lat) / (n - s)) * (G - 1)
    const fx = ((lng - w) / (e - w)) * (G - 1)
    if (fy < 0 || fy > G - 1 || fx < 0 || fx > G - 1) return null
    const i0 = Math.floor(fy)
    const j0 = Math.floor(fx)
    const i1 = Math.min(G - 1, i0 + 1)
    const j1 = Math.min(G - 1, j0 + 1)
    const v00 = grid[i0 * G + j0]
    const v01 = grid[i0 * G + j1]
    const v10 = grid[i1 * G + j0]
    const v11 = grid[i1 * G + j1]
    if (v00 == null || v01 == null || v10 == null || v11 == null) return null
    const ty = fy - i0
    const tx = fx - j0
    const top = v00 + (v01 - v00) * tx
    const bot = v10 + (v11 - v10) * tx
    return top + (bot - top) * ty
  }

  let below = 0
  let near = 0
  let above = 0
  for (const p of samples) {
    if (p.elevation == null) continue
    const tl = treelineAt(p.lat, p.lng)
    if (tl == null) continue
    const d = p.elevation - tl
    if (d < -TREELINE_NTL_BUFFER_M) below++
    else if (d <= TREELINE_NTL_BUFFER_M) near++
    else above++
  }
  if (below + near + above === 0) return []
  return [
    { label: 'Below', value: below, color: ZONE_BELOW_COLOR },
    { label: 'Near', value: near, color: ZONE_NEAR_COLOR },
    { label: 'Above', value: above, color: ZONE_ABOVE_COLOR },
  ]
}

/**
 * Synchronous skeleton View: title + cheap geometric stats (distance / area /
 * location) and empty distributions. Shown instantly so the bar appears while
 * the (slow) terrain sampling fills in the profile and widgets.
 */
export function buildGeometrySkeleton(
  geometry: GeoJSON.Geometry,
  title: string,
  unitSystem: UnitSystem,
  ephemeral = false
): GeometryView {
  if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
    const meters = turfLength(lineString(lineCoords(geometry)), { units: 'meters' })
    return {
      type: 'line',
      title,
      stats: [{ label: 'Distance', value: convertDistance(meters, unitSystem) }],
      ...EMPTY_DISTRIBUTIONS,
      aspectCounts: [...ZERO_ASPECT],
      pending: {
        profile: true,
        elevation: true,
        slope: true,
        aspect: true,
        landCover: true,
        treeline: true,
      },
      ephemeral,
    }
  }
  if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
    const rings =
      geometry.type === 'Polygon' ? geometry.coordinates : (geometry.coordinates[0] ?? [])
    const sqMeters = turfArea(
      turfPolygon(geometry.type === 'Polygon' ? geometry.coordinates : rings)
    )
    return {
      type: 'polygon',
      title,
      stats: [{ label: 'Area', value: convertArea(sqMeters, unitSystem) }],
      ...EMPTY_DISTRIBUTIONS,
      aspectCounts: [...ZERO_ASPECT],
      pending: {
        profile: false,
        elevation: true,
        slope: true,
        aspect: true,
        landCover: true,
        treeline: true,
      },
      ephemeral,
    }
  }
  const [lng, lat] = geometry.type === 'Point' ? geometry.coordinates : [0, 0]
  return {
    type: 'point',
    title,
    stats: [{ label: 'Location', value: `${lat.toFixed(5)}, ${lng.toFixed(5)}` }],
    ...EMPTY_DISTRIBUTIONS,
    aspectCounts: [...ZERO_ASPECT],
    pending: { ...NO_PENDING },
    ephemeral,
  }
}

/**
 * Phase 2 for line/area: land cover and treeline zones are independent reads
 * (treeline comes from its own COG, not from land cover). Resolve them
 * concurrently but surface each the instant it settles via `onProgress`, so a
 * slow or failing treeline read never blocks the land-cover widget, and vice
 * versa. The awaited result is the fully-resolved View.
 */
async function resolveLandCoverAndTreeline(
  demView: GeometryView,
  stats: GeometrySummaryItem[],
  pts: { lat: number; lng: number }[],
  geometry: GeoJSON.Geometry,
  samplePoints: { lat: number; lng: number; elevation: number | null }[],
  landCover: LandCoverSampler,
  treeline: TreelineSampler,
  onProgress?: (view: GeometryView) => void
): Promise<GeometryView> {
  let classes: number[] | null = null
  let zones: DistributionBin[] = []
  let lcPending = true
  let tlPending = true
  const merge = (): GeometryView => {
    const tree = classes ? treeCoverPct(classes) : null
    return {
      ...demView,
      stats: tree != null ? [...stats, { label: 'Tree cover', value: `${tree}%` }] : stats,
      landCover: classes ? buildLandCover(classes) : [],
      treelineZones: zones,
      pending: { ...NO_PENDING, landCover: lcPending, treeline: tlPending },
    }
  }
  await Promise.all([
    gatherLandCover(pts, landCover)
      .then(c => {
        classes = c
      })
      .catch(() => {})
      .finally(() => {
        lcPending = false
        onProgress?.(merge())
      }),
    buildTreelineZones(geometry, samplePoints, treeline)
      .then(z => {
        zones = z
      })
      .catch(() => {})
      .finally(() => {
        tlPending = false
        onProgress?.(merge())
      }),
  ])
  return merge()
}

/**
 * Build the full View payload for a geometry, sampling terrain as needed.
 *
 * Two phases: the fast DEM widgets (profile, elevation, slope, aspect) resolve
 * first and are emitted via `onProgress`; the slow land-cover widget lands last.
 * `onProgress` receives each intermediate View; the awaited result is the final.
 */
export async function buildGeometryView(
  geometry: GeoJSON.Geometry,
  title: string,
  unitSystem: UnitSystem,
  terrain: TerrainSampler,
  landCover: LandCoverSampler,
  treeline: TreelineSampler,
  opts: { ephemeral?: boolean } = {},
  onProgress?: (view: GeometryView) => void
): Promise<GeometryView> {
  const ephemeral = opts.ephemeral ?? false

  if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
    const coords = lineCoords(geometry)
    const meters = turfLength(lineString(coords), { units: 'meters' })
    const pts = sampleLine(coords, PROFILE_SAMPLE_M, PROFILE_MIN_SAMPLES, LINE_MAX_SAMPLES)

    // Phase 1 — DEM
    const { elevations, slopes, aspects } = await gatherDEM(pts, terrain)
    const profile: ProfilePoint[] = []
    for (let i = 0; i < pts.length; i++) {
      if (elevations[i] != null)
        profile.push({ distance: pts[i].distance, elevation: elevations[i]! })
    }
    const elevValues = elevations.filter((e): e is number => e != null)
    const stats: GeometrySummaryItem[] = [
      { label: 'Distance', value: convertDistance(meters, unitSystem) },
    ]
    // Straight-line heading start→end — the "angle to target". Shown for
    // ephemeral measures (the point of measuring); on a saved winding trail the
    // start→end bearing is mostly noise, so it's left off there.
    if (ephemeral && coords.length >= 2) {
      const head = turfBearing(coords[0], coords[coords.length - 1])
      stats.push({ label: 'Bearing', value: formatBearing(head) })
    }
    if (profile.length >= 2) {
      stats.push({ label: 'Est. time', value: formatDuration(estimateHikeSeconds(profile)) })
      const { ascent, descent } = computeGainLoss(profile)
      stats.push({ label: 'Ascent', value: convertElevation(ascent, unitSystem) })
      stats.push({ label: 'Descent', value: convertElevation(descent, unitSystem) })
    }
    if (slopes.length) {
      stats.push({ label: 'Max slope', value: `${Math.round(Math.max(...slopes))}°` })
    }
    const elevHist = buildElevationHistogram(elevValues, unitSystem)
    const demView: GeometryView = {
      type: 'line',
      title,
      stats,
      profile,
      elevationHistogram: elevHist.bins,
      elevationAxis: elevHist.axis,
      slopeHistogram: buildSlopeHistogram(slopes),
      aspectCounts: buildAspectCounts(aspects),
      landCover: [],
      treelineZones: [],
      pending: { ...NO_PENDING, landCover: true, treeline: true },
      ephemeral,
    }
    onProgress?.(demView)

    // Phase 2 — land cover + treeline zones, each surfaced independently.
    const samplePoints = pts.map((p, i) => ({ lat: p.lat, lng: p.lng, elevation: elevations[i] }))
    return resolveLandCoverAndTreeline(
      demView,
      stats,
      pts,
      geometry,
      samplePoints,
      landCover,
      treeline,
      onProgress
    )
  }

  if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
    const rings =
      geometry.type === 'Polygon' ? geometry.coordinates : (geometry.coordinates[0] ?? [])
    const poly = turfPolygon(geometry.type === 'Polygon' ? geometry.coordinates : rings)
    const sqMeters = turfArea(poly)
    const outer = (rings[0] ?? []) as number[][]
    const perimeterM = outer.length >= 2 ? turfLength(lineString(outer), { units: 'meters' }) : 0
    const pts = samplePolygon(geometry)

    // Phase 1 — DEM
    const { elevations, slopes, aspects } = await gatherDEM(pts, terrain)
    const elevValues = elevations.filter((e): e is number => e != null)
    const stats: GeometrySummaryItem[] = [
      { label: 'Area', value: convertArea(sqMeters, unitSystem) },
    ]
    if (perimeterM > 0) {
      stats.push({ label: 'Perimeter', value: convertDistance(perimeterM, unitSystem) })
    }
    if (elevValues.length) {
      stats.push({
        label: 'Elevation',
        value: `${convertElevation(Math.min(...elevValues), unitSystem)}–${convertElevation(
          Math.max(...elevValues),
          unitSystem
        )}`,
      })
    }
    if (slopes.length) {
      const mean = slopes.reduce((a, b) => a + b, 0) / slopes.length
      stats.push({ label: 'Mean slope', value: `${Math.round(mean)}°` })
    }
    const elevHist = buildElevationHistogram(elevValues, unitSystem)
    const demView: GeometryView = {
      type: 'polygon',
      title,
      stats,
      profile: [],
      elevationHistogram: elevHist.bins,
      elevationAxis: elevHist.axis,
      slopeHistogram: buildSlopeHistogram(slopes),
      aspectCounts: buildAspectCounts(aspects),
      landCover: [],
      treelineZones: [],
      pending: { ...NO_PENDING, landCover: true, treeline: true },
      ephemeral,
    }
    onProgress?.(demView)

    // Phase 2 — land cover + treeline zones, each surfaced independently.
    const samplePoints = pts.map((p, i) => ({ lat: p.lat, lng: p.lng, elevation: elevations[i] }))
    return resolveLandCoverAndTreeline(
      demView,
      stats,
      pts,
      geometry,
      samplePoints,
      landCover,
      treeline,
      onProgress
    )
  }

  // Point
  const [lng, lat] = geometry.type === 'Point' ? geometry.coordinates : [0, 0]
  const elevation = await terrain.getElevation(lat, lng)
  const stats: GeometrySummaryItem[] = [
    { label: 'Location', value: `${lat.toFixed(5)}, ${lng.toFixed(5)}` },
  ]
  if (elevation != null) {
    stats.push({ label: 'Elevation', value: convertElevation(elevation, unitSystem) })
  }
  return {
    type: 'point',
    title,
    stats,
    ...EMPTY_DISTRIBUTIONS,
    aspectCounts: [...ZERO_ASPECT],
    pending: { ...NO_PENDING },
    ephemeral,
  }
}
